import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Typography } from '@mui/material';
import {
    GridCellParams,
    GridColDef,
    GridComparatorFn,
    GridFilterItem,
    GridRenderCellParams,
    GridValueFormatterParams,
    getGridSingleSelectOperators,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InsurancePeriod } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedIconButton as IconButton,
    TrackedDataGrid as StyledDataGrid,
} from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import PeriodModal from './PeriodModal';

export const COVERAGE_TYPES = {
    us: ['AL', 'APD', 'MTC'],
    il: ['COMPULSORY', 'CASCO', 'COMPREHENSIVE'],
} as const;

const coverageTypeSortComparator: GridComparatorFn<string[]> = (coverageType1: string[], coverageType2: string[]) => {
    return coverageType1.length - coverageType2.length;
};

const coverageTypeFilterOperators = getGridSingleSelectOperators()
    .filter((operator) => operator.value === 'isAnyOf')
    .map((operator) => {
        const newOperator = { ...operator };
        const newGetApplyFilterFn = (filterItem: GridFilterItem) => {
            return (params: GridCellParams): boolean => {
                const value = params.value as string[];
                let isOk = true;
                filterItem?.value?.forEach((item: string) => {
                    isOk = isOk && value.includes(item);
                });
                return isOk;
            };
        };
        newOperator.getApplyFilterFn = newGetApplyFilterFn;
        return newOperator;
    });

interface InsurancePeriodsProps {
    periods: InsurancePeriod[];
    isPeriodModalOpen: boolean;
    setIsPeriodModalOpen: CallableFunction;
    setInsurancePeriods: CallableFunction;
    setAlert: CallableFunction;
}

const InsurancePeriods: React.FC<InsurancePeriodsProps> = (props: InsurancePeriodsProps) => {
    const [selectedPeriodId, setSelectedPeriodId] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { agencyApi } = useApi();
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();

    const selectedPeriod = useMemo(() => {
        return props.periods.find((period) => period.id === selectedPeriodId);
    }, [selectedPeriodId, props.periods]);

    const handleSavePeriod = (period: InsurancePeriod) => {
        setIsLoading(true);
        agencyApi
            .agencyV0PeriodPost({ insurancePeriod: period })
            .then((res) => {
                props.setInsurancePeriods([...props.periods, res.data]);
                setIsLoading(false);
                props.setIsPeriodModalOpen(false);
                setSelectedPeriodId(undefined);
            })
            .catch(() => {
                props.setAlert({ message: t('content.loss_runs.error_adding_period'), type: 'error', duration: 6000 });
                setIsLoading(false);
            });
    };

    const handleUpdatePeriod = (period: InsurancePeriod) => {
        setIsLoading(true);
        agencyApi
            .agencyV0PeriodPeriodIdPatch({ insurancePeriod: period, periodId: period.id })
            .then(() => {
                const updatedPeriods = props.periods.map((p) => (p.id === period.id ? period : p));
                props.setInsurancePeriods(updatedPeriods);
                setIsLoading(false);
                props.setIsPeriodModalOpen(false);
                setSelectedPeriodId(undefined);
            })
            .catch(() => {
                props.setAlert({
                    message: t('content.loss_runs.error_updating_period'),
                    type: 'error',
                    duration: 6000,
                });
                setIsLoading(false);
            });
    };

    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: 'startDate',
                headerName: t('content.loss_runs.period_table.start_date'),
                type: 'date',
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams) =>
                    dayjs(params.value as string).format(profile.dateFormat),
            },
            {
                field: 'endDate',
                headerName: t('content.loss_runs.period_table.end_date'),
                type: 'date',
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams) =>
                    dayjs(params.value as string).format(profile.dateFormat),
            },
            {
                field: 'coverageType',
                headerName: t('content.loss_runs.period_table.coverage_type'),
                type: 'singleSelect',
                flex: 1,
                valueOptions: COVERAGE_TYPES[profile.customer.settings.country as 'us' | 'il'],
                sortComparator: coverageTypeSortComparator,
                filterOperators: coverageTypeFilterOperators,
                renderCell: (params: GridRenderCellParams) => {
                    const value = params.value as string;
                    return (
                        <Typography variant="body2">
                            {value
                                .split(',')
                                .map((coverageType) =>
                                    t(`content.loss_runs.period_table.${coverageType.toLocaleLowerCase()}`),
                                )
                                .join(', ')}
                        </Typography>
                    );
                },
            },
            {
                field: 'carrier',
                headerName: t('content.loss_runs.period_table.carrier'),
                flex: 1,
            },
            {
                field: 'policyNumber',
                headerName: t('content.loss_runs.period_table.policy_number'),
                flex: 1,
            },
            {
                field: 'actions',
                headerName: t('content.loss_runs.period_table.actions'),
                type: 'action',
                width: 100,
                renderCell: (params: GridValueFormatterParams) => {
                    return (
                        <Box sx={{ display: 'flex' }}>
                            <IconButton
                                id={`edit-${params.id}`}
                                data-testid={`edit-${params.id}`}
                                onClick={() => setSelectedPeriodId(params.id as number)}
                            >
                                <EditIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ] as GridColDef[];
    }, []);

    const rows = useMemo(() => {
        return props.periods.map((period) => {
            return {
                id: period.id,
                startDate: dayjs(period.start_date),
                endDate: dayjs(period.end_date),
                coverageType: period.coverage_type,
                carrier: period.carrier,
                policyNumber: period.policy_number,
            };
        });
    }, [props.periods]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 2 }}>
            {props.isPeriodModalOpen || !!selectedPeriod ? (
                <PeriodModal
                    isLoading={isLoading}
                    periods={props.periods}
                    selectedPeriod={selectedPeriod}
                    onSave={(period: InsurancePeriod) => {
                        if (!!selectedPeriod) {
                            handleUpdatePeriod(period);
                        } else {
                            handleSavePeriod(period);
                        }
                    }}
                    onClose={() => {
                        props.setIsPeriodModalOpen(false);
                        setSelectedPeriodId(undefined);
                    }}
                />
            ) : null}
            <Button
                id="btn-add-period"
                data-testid="btn-add-period"
                variant="contained"
                color="secondary"
                onClick={() => props.setIsPeriodModalOpen(true)}
                sx={{ mb: 2 }}
                startIcon={<AddIcon />}
            >
                {t('content.loss_runs.add_period')}
            </Button>
            <StyledDataGrid
                id="insurance-periods-data-grid"
                data-testid="insurance-periods-data-grid"
                columns={columns}
                columnBuffer={columns.length}
                rows={rows}
                disableRowSelectionOnClick
                hideFooter
                disableVirtualization={process.env.JEST_WORKER_ID !== undefined}
                slots={{
                    noRowsOverlay: () => (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="overline">{t('content.loss_runs.period_table.no_periods')}</Typography>
                        </Box>
                    ),
                }}
                slotProps={{
                    columnsPanel: {
                        sx: {
                            '& .MuiDataGrid-panelFooter button:first-child': {
                                display: 'none',
                            },
                        },
                    },
                }}
                localeText={{
                    ...gridLocalization[i18n.languages[0]],
                    filterPanelRemoveAll: t('table.remove_all'),
                    columnMenuManageColumns: t('table.manage_columns'),
                    unpin: t('table.unpin'),
                }}
                columnHeaderHeight={42}
                sx={{
                    height: '100%',
                    minHeight: rows.length === 0 ? '250px' : 0,
                    width: '100%',
                    maxWidth: '750px',
                    pb: 2,
                    '& .MuiDataGrid-main': {
                        borderRadius: '4px',
                        boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        color: palette.neutral[100],
                        '& .MuiSvgIcon-root': {
                            fill: palette.neutral[100],
                        },
                    },
                    '& .MuiDataGrid-cell--editing': {
                        padding: '0 !important',
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row--dynamicHeight': { minHeight: '52px !important' },
                    '&.MuiDataGrid-root .MuiDataGrid-cell': { py: '6px' },
                    backgroundColor: palette.neutral[50],
                }}
            />
        </Box>
    );
};

export default InsurancePeriods;
